import { useEffect, useState } from "react";
import api from '../../utils/api';
import dayjs from 'dayjs';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

//Page Components
import HighlightedCard from './components/HighlightedCard';
import StudentsRegistrationChart from './components/StudentsRegistrationChart';

//Icons
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

const dateRanges = [
    { label: 'Last 7 Days', value: '7_days', format: 'day' },
    { label: 'Last 30 Days', value: '30_days', format: 'day' },
    { label: 'Last Year', value: 'year', format: 'month' },
];

const getFormatForRange = (rangeValue) => {
    const range = dateRanges.find(r => r.value === rangeValue);
    return range?.format || 'day';
};

export default function Dashboard(props) {
    const [dashboardData, setDashboardData] = useState({
        students: { count: 0 },
        teachers: { count: 0 },
        courses: { count: 0 },
        subscribers: { count: 0 },
        sms_providers: { count: 0 },
        lists: { count: 0, email_contacts: 0, sms_contacts: 0 }
    });

    const [studentsChartData, setStudentsChartData] = useState(null);
    const [studentsChartLoading, setStudentsChartLoading] = useState(false);
    const [studentsChartRange, setStudentsChartRange] = useState('30_days');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    useEffect(() => {
        fetchStudentsRegistrations();
    }, [studentsChartRange]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchDashboardData = async () => {
        try {
            const response = await api.get('admin/dashboard/stats');
            if (response.data.success && response.data.data) {
                setDashboardData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
    };

    const fetchStudentsRegistrations = async () => {
        try {
            setStudentsChartLoading(true);
            const now = dayjs();
            let start, end, period;
            switch (studentsChartRange) {
                case '7_days':
                    start = now.clone().subtract(7, 'day');
                    end = now;
                    period = getFormatForRange('7_days');
                    break;
                case '30_days':
                    start = now.clone().subtract(30, 'day');
                    end = now;
                    period = getFormatForRange('30_days');
                    break;
                case 'year':
                    start = now.clone().subtract(1, 'year');
                    end = now;
                    period = getFormatForRange('year');
                    break;
                default:
                    start = now.clone().subtract(30, 'day');
                    end = now;
                    period = 'day';
            }
            const response = await api.get('admin/dashboard/student-registrations', {
                params: {
                    from: start.format('YYYY-MM-DD'),
                    to: end.format('YYYY-MM-DD'),
                    format: period
                }
            });
            if (response.data.success && response.data.data?.statistics) {
                setStudentsChartData(response.data.data.statistics);
            }
        } catch (error) {
            console.error('Error fetching student registrations:', error);
        } finally {
            setStudentsChartLoading(false);
        }
    };

    const cards = [
        {
            title: 'Students',
            value: (dashboardData.students?.count ?? 0).toLocaleString(),
            interval: 'Registered students',
            icon: SchoolIcon,
            iconColor: 'primary.main'
        },
        {
            title: 'Teachers',
            value: (dashboardData.teachers?.count ?? 0).toLocaleString(),
            interval: 'Teaching staff',
            icon: PersonIcon,
            iconColor: 'success.main'
        },
        {
            title: 'Courses',
            value: (dashboardData.courses?.count ?? 0).toLocaleString(),
            interval: 'Available courses',
            icon: MenuBookIcon,
            iconColor: 'warning.main'
        },
        {
            title: 'Subscribers',
            value: (dashboardData.subscribers?.count ?? 0).toLocaleString(),
            interval: 'Newsletter & updates subscribers',
            icon: SubscriptionsIcon,
            iconColor: 'info.main'
        }
    ];

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Overview
            </Typography>
            <Grid
                container
                spacing={2}
                columns={12}
                sx={{ mb: (theme) => theme.spacing(2) }}
            >
                {cards.map((card, index) => (
                    <Grid key={index} size={{ xs: 6, sm: 6, lg: 3 }}>
                        <HighlightedCard {...card} />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item size={{ xs: 12, md:6 }}>
                    <StudentsRegistrationChart
                        statistics={studentsChartData}
                        loading={studentsChartLoading}
                        title="New Students"
                        onDateRangeChange={setStudentsChartRange}
                        selectedRange={studentsChartRange}
                        format={getFormatForRange(studentsChartRange)}
                    />
                </Grid>

                <Grid item size={{ xs: 12, md:6 }}>
                    <StudentsRegistrationChart
                        statistics={studentsChartData}
                        loading={studentsChartLoading}
                        title="Courses Purchase"
                        onDateRangeChange={setStudentsChartRange}
                        selectedRange={studentsChartRange}
                        format={getFormatForRange(studentsChartRange)}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
